/**
 * The frame every agent page sits in when it is opened inside the app.
 *
 * Deliberately thin. The pages already carry their own chrome (AgentPageLayout,
 * Aurora Glass), so all this adds is the one thing a token page does not need:
 * a session. Without one there is nobody for agent-action-respond to resolve,
 * so the person is sent to sign in and brought back to the page they wanted.
 *
 * Signed in, the location must still be one the person can open (spec
 * Addendum A, A10/R26/A77/R08). A deleted location's old links (an email, a
 * WhatsApp, Back after a delete, a stale switcher row) used to reach a page
 * whose ops answered 401, which sent a signed-in owner to sign in. Now:
 *
 *   1. the location is in the caller's app_overview (fetchOverview(false):
 *      the held answer while it is under a minute old, else one read): the
 *      page opens. A held overview of any age is not trusted on its own, see
 *      locationAccess.ts; a check that said yes is trusted for a minute.
 *   2. it is not: the overview only lists the workspaces the caller owns or is
 *      a member of (and at most 500 locations), while an ops executive and
 *      Fly's staff may open others. So the server is asked, with the session
 *      (agent-action-respond page_links, the read every page makes first
 *      anyway): it answers, the page opens; it refuses (401, 403, 404), the
 *      page says "This location is no longer in your account. It may have
 *      been deleted." with Go to Manage all locations. The server also answers
 *      401 when its own lookup failed, so a 401 is asked about once more first.
 *   3. anything else (offline, an overview that could not be read) opens the
 *      page, which says what went wrong itself: a blip never hides a page.
 *
 * Support chat (WO-69, Anand, 13 Oct 26): while the shell is up, support sees
 * the location and its workspace (useLocationSupportChat). The Crisp launcher
 * stays tucked: on a phone the tab bar's Chat fan holds it (Shardul, 14 Oct
 * 26), from a tablet up "Chat with Fly support" in the sidebar and the rail
 * opens it (Anand, 15 Oct 26). The shared links never pass through this shell.
 *
 * Clean addresses (23 Oct 26, docs/agent-clean-addresses-plan.md): on
 * /app/<slug>/<page> there is no :locationId in the route, so the frame that
 * resolved the slug (AppSlugShell) hands the id in as `locationId`. Without the
 * prop the route parameter is read, exactly as before.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { signInPath } from '@/lib/agentCaller';
import { FunnelLoader } from '@/components/agent-shell/FunnelBoundary';
import { fetchOverview, knownSessionUser } from '../appData';
import type { AppOverview } from '../appModel';
import { recentlyConfirmed, rememberLocationOpens } from './locationAccess';
import { useLocationSupportChat } from './useLocationSupportChat';
import '@/styles/aurora-pages.css';
import '@/styles/agent-app-support-chat.css';

type SessionState = 'checking' | 'in' | 'out';
export type LocationAccess = 'ok' | 'gone';

// Shardul, 08 Oct 26: another account's location opened after a switch and Back. The page now says plainly that this
// account cannot open it, whether it belongs to another account or was deleted, and offers both ways on.
export const GONE_TITLE = 'This location is not on this account.';
export const GONE_LINE = 'It belongs to another account, or it was deleted.';
export const GONE_ACTION = 'Go to Manage all locations';
/** Signs this account out and asks for another, coming back to this page (lib/accountSwitch.ts drops the tab's copies). */
export const GONE_SWITCH = 'Use another account';

function holds(overview: AppOverview | null | undefined, locationId: string): boolean {
  return !!overview?.workspaces.some((w) => w.locations.some((l) => l.location_id === locationId));
}

/** Already known to open for the signed-in person, with no read: a check said so under a minute ago. */
function knownOpen(locationId: string): boolean {
  return recentlyConfirmed(locationId, knownSessionUser());
}

export { resetLocationAccessMemo } from './locationAccess';

/** What the server's refusal status was, when it sent one. */
function refusedStatus(error: unknown): number | null {
  const status = (error as { context?: { status?: unknown } } | null)?.context?.status;
  return typeof status === 'number' ? status : null;
}

/** Asks the server about one location: 'ok' when it opens it, else the refusal status (null when none came). */
async function probe(locationId: string): Promise<'ok' | number | null> {
  const { error } = await supabase.functions.invoke('agent-action-respond', { body: { op: 'page_links', location_id: locationId } });
  return error ? refusedStatus(error) : 'ok';
}

/** Steps 1 to 3 above, for a signed-in person. */
export async function checkLocationAccess(locationId: string): Promise<LocationAccess> {
  const answer = await fetchOverview(false);
  if (answer.kind !== 'ok') {
    console.log('[AppLocationShell] overview not read, opening the page', { location_id: locationId, kind: answer.kind });
    return 'ok';
  }
  if (holds(answer.value, locationId)) {
    rememberLocationOpens(locationId, answer.userId);
    return 'ok';
  }
  let status = await probe(locationId);
  if (status === 401) {
    console.log('[AppLocationShell] 401 on the check, asking once more (it also means the server lookup failed)', { location_id: locationId });
    status = await probe(locationId);
  }
  if (status === 'ok') {
    console.log('[AppLocationShell] not in the overview, but the server opens it (ops, staff or past 500)', { location_id: locationId });
    rememberLocationOpens(locationId, answer.userId);
    return 'ok';
  }
  if (status === 401 || status === 403 || status === 404) {
    console.log('[AppLocationShell] location not in this account', { location_id: locationId, status });
    return 'gone';
  }
  console.warn('[AppLocationShell] could not check the location, opening the page', { location_id: locationId, status });
  return 'ok';
}

/** "This location is not on this account.", shared with the slug frame for a slug that does not resolve. */
export function GonePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [leaving, setLeaving] = useState(false);
  const useAnother = async () => {
    setLeaving(true);
    const { error } = await supabase.auth.signOut();
    if (error) console.warn('[AppLocationShell] sign out for another account failed', error.message);
    console.log('[AppLocationShell] signed out to use another account', { back_to: location.pathname });
    navigate(signInPath(`${location.pathname}${location.search}${location.hash}`), { replace: true });
  };
  return (
    <div className="ag-standalone flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="ag-standalone-card p-6 text-center" role="status">
          <h1 className="mb-2 text-xl font-semibold ag-standalone-ink">{GONE_TITLE}</h1>
          <p className="mb-6">{GONE_LINE}</p>
          <div className="flex flex-col gap-2">
            <Link to="/app" className="ag-standalone-btn ag-focus">
              {GONE_ACTION}
            </Link>
            <button type="button" className="ag-standalone-btn-secondary ag-focus w-full" disabled={leaving} onClick={() => void useAnother()}>
              {GONE_SWITCH}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AppLocationShell({ children, locationId: given }: { children: ReactNode; locationId?: string }) {
  const params = useParams<{ locationId: string }>();
  const locationId = given ?? params.locationId;
  const location = useLocation();
  const [session, setSession] = useState<SessionState>('checking');
  // The last check, per location id: the shell stays mounted when the switcher moves to another location.
  const [access, setAccess] = useState<{ id: string; state: LocationAccess } | null>(null);
  // Support chat names this location and its workspace (WO-69).
  useLocationSupportChat(locationId ?? '');

  useEffect(() => {
    let alive = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (alive) setSession(data.session ? 'in' : 'out');
    });
    const { data } = supabase.auth.onAuthStateChange((_event, next) => {
      if (alive) setSession(next ? 'in' : 'out');
    });
    return () => {
      alive = false;
      data.subscription.unsubscribe();
    };
  }, []);

  // The answer for the location in the address: one checked for it, else one already known to open.
  const state: LocationAccess | null = !locationId ? null : access?.id === locationId ? access.state : knownOpen(locationId) ? 'ok' : null;
  const known = state !== null;
  useEffect(() => {
    if (session !== 'in' || !locationId || known) return undefined;
    let alive = true;
    void checkLocationAccess(locationId).then((answer) => {
      if (alive) setAccess({ id: locationId, state: answer });
    });
    return () => {
      alive = false;
    };
  }, [session, locationId, known]);

  if (!locationId) return <Navigate to="/gbp-growth-agent" replace />;

  // The app's own page colour while the session is read (white, or the dark
  // theme's when this browser saved it), not a slate spinner on the site's
  // background (redesign, DESIGN.md 5, S).
  if (session === 'checking') {
    return <FunnelLoader label="Opening" />;
  }

  // The whole address goes along, anchor included, so the owner comes back to
  // the card a WhatsApp or an email linked (Anand, 26 Sep 26).
  if (session === 'out') {
    return <Navigate to={signInPath(`${location.pathname}${location.search}${location.hash}`)} replace />;
  }

  if (!known) return <FunnelLoader label="Opening" />;
  if (state === 'gone') return <GonePage />;

  return <>{children}</>;
}

export default AppLocationShell;
